import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { companyAPI } from '../services/api';
import { Company } from '../types';
import { useCompany } from '../context/CompanyContext';
import { useAuth } from '../context/AuthContext';
import { useI18n } from '../hooks/useI18n';
import { useOnboardingTour } from '../hooks/useOnboardingTour';
import Layout from '../components/Layout';
import { Plus, Edit, Trash2, Eye, Search, Check, AlertCircle, CheckCircle, Upload, X, Building2, Lock, HelpCircle } from 'lucide-react';


const CompanyListPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, isRTL } = useI18n();
  const { startTour, manualStart } = useOnboardingTour();
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingCompany, setViewingCompany] = useState<Company | null>(null);
  const [viewLoading, setViewLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const {
    selectedCompany: contextSelectedCompany,
    setSelectedCompany: setContextSelectedCompany,
    companies,
    loading,
    loadCompanies
  } = useCompany();

  // Check for success message from navigation
  useEffect(() => {
    if (location.state?.message) {
      setSuccessMessage(location.state.message);
      // Clear the state to prevent showing the message again on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  // Auto-start onboarding tour on first visit
  useEffect(() => {
    const timer = setTimeout(() => {
      startTour({
        tourId: 'company-list',
        steps: [
          {
            element: '#company-list-create-btn',
            popover: {
              title: t('onboarding.companyList.createBtnTitle'),
              description: t('onboarding.companyList.createBtnDesc'),
              side: 'bottom' as const,
              align: 'end' as const,
            },
          },
          {
            element: '#company-list-table',
            popover: {
              title: t('onboarding.companyList.tableTitle'),
              description: t('onboarding.companyList.tableDesc'),
              side: 'top' as const,
              align: 'start' as const,
            },
          },
        ],
        autoStartDelay: 0,
      });
    }, 800);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDeleteCompany = async (companyId: string) => {
    if (window.confirm('Are you sure you want to delete this company?')) {
      try {
        await companyAPI.deleteCompany(companyId);
        // Refresh the companies list from context
        await loadCompanies();
        setError(null);
        setSuccessMessage('Company deleted successfully');
      } catch (err) {
        setError('Failed to delete company');
        console.error('Error deleting company:', err);
      }
    }
  };

  // Handler to view company with full details
  const handleViewCompany = async (company: Company) => {
    try {
      setViewLoading(true);
      const fullCompany = await companyAPI.getCompanyById(company.id, true);
      setViewingCompany(fullCompany);
      setShowViewModal(true);
    } catch (err) {
      console.error('Failed to load company details:', err);
      // Fallback to summary (logo will be missing)
      setViewingCompany(company as Company);
      setShowViewModal(true);
    } finally {
      setViewLoading(false);
    }
  };



  const filteredCompanies = companies.filter(company =>
    company.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    company.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    company.phone?.includes(searchTerm)
  );

  // Helper function to safely convert logo data to base64
  const getLogoBase64 = (logo: number[] | undefined): string | null => {
    if (!logo || !Array.isArray(logo) || logo.length === 0) {
      return null;
    }
    try {
      return btoa(String.fromCharCode.apply(null, logo));
    } catch (error) {
      console.error('Error converting logo to base64:', error);
      return null;
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-64">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
        </div>
      </Layout>
    );
  }

  if (!contextSelectedCompany?.selectedCompanyId) {
    return (
      <Layout>
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold text-gray-900">{t('companies.title')}</h1>
          <div className="flex gap-2">
            <button
              onClick={() => navigate('/companies/create')}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2"
            >
              <Plus size={20} />
              {t('companies.addCompany')}
            </button>
          </div>
        </div>
        <div className="flex items-center justify-center min-h-64">
          <div className="text-center">
            <AlertCircle className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">{t('clients.noCompanySelected')}</h2>
            <p className="text-gray-600 mb-4">{t('clients.noCompanyMessage')}</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className={`min-h-screen ${isRTL ? 'rtl' : 'ltr'}`} dir={isRTL ? 'rtl' : 'ltr'}>
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold text-gray-900">{t('companies.title')}</h1>
          <div className="flex gap-2">
            <button
              id="company-list-create-btn"
              onClick={() => navigate('/companies/create')}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2"
            >
              <Plus size={20} />
              {t('companies.addCompany')}
            </button>
          </div>
        </div>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        {successMessage && (
          <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4 flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <CheckCircle className="w-5 h-5" />
              <span>{successMessage}</span>
            </div>
            <button
              onClick={() => setSuccessMessage(null)}
              className="text-green-700 hover:text-green-900"
            >
              ×
            </button>
          </div>
        )}

        <div className="mb-6">
          <div className="relative">
            <Search className={`absolute ${isRTL ? 'right-3' : 'left-3'} top-1/2 transform -translate-y-1/2 text-gray-400 z-10`} size={20} />
            <input
              type="text"
              placeholder={t('companies.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={`w-full ${isRTL ? 'pr-10 pl-4' : 'pl-10 pr-4'} py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
            />
          </div>
        </div>

        <div id="company-list-table" className="bg-white shadow-md rounded-lg overflow-hidden overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className={`px-6 py-3 ${isRTL ? 'text-right' : 'text-left'} text-xs font-medium text-gray-500 uppercase tracking-wider`}>
                  {t('companies.name')}
                </th>
                <th className={`hidden md:table-cell px-6 py-3 ${isRTL ? 'text-right' : 'text-left'} text-xs font-medium text-gray-500 uppercase tracking-wider`}>
                  {t('companies.email')}
                </th>
                <th className={`hidden md:table-cell px-6 py-3 ${isRTL ? 'text-right' : 'text-left'} text-xs font-medium text-gray-500 uppercase tracking-wider`}>
                  {t('companies.phone')}
                </th>
                <th className={`hidden md:table-cell px-6 py-3 ${isRTL ? 'text-right' : 'text-left'} text-xs font-medium text-gray-500 uppercase tracking-wider`}>
                  {t('companies.city')}
                </th>
                <th className={`hidden md:table-cell px-6 py-3 ${isRTL ? 'text-right' : 'text-left'} text-xs font-medium text-gray-500 uppercase tracking-wider`}>
                  {t('companies.vatRate')}
                </th>
                <th className={`px-6 py-3 ${isRTL ? 'text-right' : 'text-left'} text-xs font-medium text-gray-500 uppercase tracking-wider`}>
                  {t('companies.actions')}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredCompanies.map((company) => (
                <tr key={company.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                        <Building2 className="w-5 h-5 text-gray-400" />
                      </div>
                      <div>
                        <div className="flex items-center space-x-2">
                          <div className="text-sm font-medium text-gray-900">{company.name}</div>
                          {contextSelectedCompany.selectedCompanyId === company.id && (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              Selected
                            </span>
                          )}
                        </div>
                        {company.ice && (
                          <div className="text-sm text-gray-500">ICE: {company.ice}</div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="hidden md:table-cell px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {company.email || '-'}
                  </td>
                  <td className="hidden md:table-cell px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {company.phone || '-'}
                  </td>
                  <td className="hidden md:table-cell px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {company.city || '-'}
                  </td>
                  <td className="hidden md:table-cell px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {company.vatRate ? `${company.vatRate}%` : '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className={`flex ${isRTL ? 'space-x-reverse' : 'space-x-2'}`}>
                      <button
                        onClick={() => handleViewCompany(company)}
                        className="text-blue-600 hover:text-blue-900"
                        title={t('companies.view')}
                        disabled={viewLoading}
                      >
                        <Eye size={16} />
                      </button>
                      <button
                        onClick={() => navigate(`/companies/${company.id}/edit`)}
                        className="text-green-600 hover:text-green-900"
                        title={t('companies.edit')}
                      >
                        <Edit size={16} />
                      </button>
                      <button
                        onClick={() => setContextSelectedCompany(company.id)}
                        className={`${contextSelectedCompany.selectedCompanyId === company.id
                          ? 'text-green-600 hover:text-green-900'
                          : 'text-gray-600 hover:text-gray-900'
                          }`}
                        title={contextSelectedCompany.selectedCompanyId === company.id ? t('companies.currentlySelected') : t('companies.selectCompany')}
                      >
                        <Check size={16} />
                      </button>
                      <button
                        onClick={() => handleDeleteCompany(company.id)}
                        className="text-red-600 hover:text-red-900"
                        title={t('companies.delete')}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* View Company Modal */}
        {showViewModal && viewingCompany && (
          <CompanyViewModal
            company={viewingCompany}
            onClose={() => {
              setShowViewModal(false);
              setViewingCompany(null);
            }}
          />
        )}
      </div>

      {/* Onboarding Help Button */}
      <button
        type="button"
        className="onboarding-help-btn"
        title={t('onboarding.replayTour')}
        onClick={() => manualStart({
          tourId: 'company-list',
          steps: [
            {
              element: '#company-list-create-btn',
              popover: {
                title: t('onboarding.companyList.createBtnTitle'),
                description: t('onboarding.companyList.createBtnDesc'),
                side: 'bottom' as const,
                align: 'end' as const,
              },
            },
            {
              element: '#company-list-table',
              popover: {
                title: t('onboarding.companyList.tableTitle'),
                description: t('onboarding.companyList.tableDesc'),
                side: 'top' as const,
                align: 'start' as const,
              },
            },
          ],
        })}
      >
        <HelpCircle size={22} />
      </button>
    </Layout>
  );
};

// Company View Modal Component
interface CompanyViewModalProps {
  company: Company;
  onClose: () => void;
}

const CompanyViewModal: React.FC<CompanyViewModalProps> = ({ company, onClose }) => {
  const { t, isRTL } = useI18n();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className={`bg-white rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto ${isRTL ? 'rtl' : 'ltr'}`} dir={isRTL ? 'rtl' : 'ltr'}>
        <h2 className="text-2xl font-bold mb-4">{t('companies.companyDetails')}</h2>

        <div className="space-y-4">
          {/* Company Logo */}
          <div className={`flex items-center ${isRTL ? 'space-x-reverse' : 'space-x-4'} mb-6 p-4 bg-gray-50 rounded-lg`}>
            <div className="w-20 h-20 bg-white rounded-lg flex items-center justify-center border border-gray-200">
              {company.logo ? (
                <img
                  src={`data:image/png;base64,${company.logo}`}
                  alt={`${company.name} logo`}
                  className="w-16 h-16 object-contain"
                />
              ) : (
                <Building2 className="w-10 h-10 text-gray-400" />
              )}
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">{company.name}</h3>
              <p className='text-sm text-gray-600'>{t('companies.companyLogo')}</p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className='block text-sm font-medium text-gray-700'>{t('companies.name')}</label>
              <p className="text-sm text-gray-900">{company.name}</p>
            </div>

            <div>
              <label className='block text-sm font-medium text-gray-700'>{t('companies.email')}</label>
              <p className="text-sm text-gray-900">{company.email || '-'}</p>
            </div>

            <div>
              <label className='block text-sm font-medium text-gray-700'>{t('companies.phone')}</label>
              <p className="text-sm text-gray-900">{company.phone || '-'}</p>
            </div>

            <div>
              <label className='block text-sm font-medium text-gray-700'>{t('companies.ice')}</label>
              <p className="text-sm text-gray-900">{company.ice || '-'}</p>
            </div>

            <div>
              <label className='block text-sm font-medium text-gray-700'>{t('companies.cnss')}</label>
              <p className="text-sm text-gray-900">{company.cnss || '-'}</p>
            </div>

            <div>
              <label className='block text-sm font-medium text-gray-700'>{t('companies.rc')}</label>
              <p className="text-sm text-gray-900">{company.rc || '-'}</p>
            </div>

            <div>
              <label className='block text-sm font-medium text-gray-700'>{t('companies.fi')}</label>
              <p className="text-sm text-gray-900">{company.fi || '-'}</p>
            </div>

            <div>
              <label className='block text-sm font-medium text-gray-700'>{t('companies.tradeLicense')}</label>
              <p className="text-sm text-gray-900">{company.tradLicense || '-'}</p>
            </div>

            <div>
              <label className='block text-sm font-medium text-gray-700'>{t('companies.capital')}</label>
              <p className="text-sm text-gray-900">{company.capital ? `${company.capital}` : '-'}</p>
            </div>

            <div>
              <label className='block text-sm font-medium text-gray-700'>{t('companies.vatRate')}</label>
              <p className="text-sm text-gray-900">{company.vatRate ? `${company.vatRate}%` : '-'}</p>
            </div>

            <div>
              <label className='block text-sm font-medium text-gray-700'>{t('companies.startingOffset')}</label>
              <p className="text-sm text-gray-900">{company.startingOffset ? `${company.startingOffset}` : '0'}</p>
            </div>

            <div>
              <label className='block text-sm font-medium text-gray-700'>{t('companies.clientStartingOffset')}</label>
              <p className="text-sm text-gray-900">{company.clientStartingOffset ? `${company.clientStartingOffset}` : '0'}</p>
            </div>

            <div>
              <label className='block text-sm font-medium text-gray-700'>{t('companies.supplierStartingOffset')}</label>
              <p className="text-sm text-gray-900">{company.supplierStartingOffset ? `${company.supplierStartingOffset}` : '0'}</p>
            </div>

            <div>
              <label className='block text-sm font-medium text-gray-700'>{t('companies.street')}</label>
              <p className="text-sm text-gray-900">{company.street || '-'}</p>
            </div>

            <div>
              <label className='block text-sm font-medium text-gray-700'>{t('companies.city')}</label>
              <p className="text-sm text-gray-900">{company.city || '-'}</p>
            </div>

            <div>
              <label className='block text-sm font-medium text-gray-700'>{t('companies.postalCode')}</label>
              <p className="text-sm text-gray-900">{company.postalCode || '-'}</p>
            </div>

            <div>
              <label className='block text-sm font-medium text-gray-700'>{t('companies.country')}</label>
              <p className="text-sm text-gray-900">{company.country || '-'}</p>
            </div>

            <div>
              <label className='block text-sm font-medium text-gray-700'>{t('companies.website')}</label>
              <p className="text-sm text-gray-900">{company.website || '-'}</p>
            </div>


            <div>
              <label className='block text-sm font-medium text-gray-700'>{t('companies.rib')}</label>
              <p className="text-sm text-gray-900">{company.rib || '-'}</p>
            </div>


            <div>
              <label className='block text-sm font-medium text-gray-700'>{t('companies.mailUsername')}</label>
              <p className="text-sm text-gray-900">{company.mailUsername || '-'}</p>
            </div>

            <div>
              <label className='block text-sm font-medium text-gray-700'>{t('companies.language')}</label>
              <p className="text-sm text-gray-900">{company.lang || '-'}</p>
            </div>
          </div>

          <div>
            <label className='block text-sm font-medium text-gray-700'>{t('companies.mailSubject')}</label>
            <p className="text-sm text-gray-900">{company.mailSubject || '-'}</p>
          </div>

          <div>
            <label className='block text-sm font-medium text-gray-700'>{t('companies.mailBody')}</label>
            <p className="text-sm text-gray-900">{company.mailBody || '-'}</p>
          </div>

          <div className="flex justify-end pt-4">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
            >
              {t('companies.close')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompanyListPage;
